import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

export interface PopupImageSlide { id?: string; image_url: string; image_asset_id?: string | null; alt?: string }
interface Props {
 slides: PopupImageSlide[]
 imageStyle: React.CSSProperties
 href?: string | null
 interval?: number | null
 transition?: number | null
 autoplay?: boolean | null
 controls?: boolean | null
 showPlayPause?: boolean | null
}

/** Stable first-image geometry, shared with the editor. Controls never sit inside the image link. */
export function PopupImageSlideshow({slides,imageStyle,href,interval=5000,transition=350,autoplay=true,controls=false,showPlayPause=true}:Props) {
 const root=useRef<HTMLDivElement>(null)
 const [currentUrl,setCurrentUrl]=useState(slides[0]?.image_url || '')
 const [previousUrl,setPreviousUrl]=useState<string | null>(null)
 const [revealed,setRevealed]=useState(true)
 const [ready,setReady]=useState<string[]>([])
 const [pending,setPending]=useState<{url:string;manual:boolean} | null>(null)
 const decoding=useRef(new Set<string>())
 const decoded=useRef(new Set<string>())
 const mounted=useRef(true)
 const frame=useRef<number | null>(null)
 const finishTimer=useRef<number | null>(null)
 const [paused,setPaused]=useState(false)
 const [requestedPlayback,setRequestedPlayback]=useState(false)
 const [hovered,setHovered]=useState(false)
 const [focused,setFocused]=useState(false)
 const [visible,setVisible]=useState(true)
 const [reduced,setReduced]=useState(false)
 const [failed,setFailed]=useState<string[]>([])
 const signature=slides.map(slide=>slide.image_url).join('\n')
 const valid=slides.filter(slide=>slide.image_url && !failed.includes(slide.image_url)).slice(0,12)
 const current=Math.max(0,valid.findIndex(slide=>slide.image_url===currentUrl))
 const activeUrl=valid[current]?.image_url || ''
 const manualPause=paused || (autoplay===false && !requestedPlayback) || (reduced && !requestedPlayback)
 const canPlay=valid.length>1 && !manualPause && !hovered && (!focused || requestedPlayback) && visible
 const delay=Math.min(30000,Math.max(1000,Number(interval)||5000))
 const fade=reduced?0:Math.min(1000,Math.max(0,Number(transition)||0))

 const clearTransition=useCallback(()=>{
  if(frame.current!==null)window.cancelAnimationFrame(frame.current)
  if(finishTimer.current!==null)window.clearTimeout(finishTimer.current)
  frame.current=null;finishTimer.current=null
 },[])
 useEffect(()=>{mounted.current=true;return ()=>{mounted.current=false;clearTransition()}},[clearTransition])
 useEffect(()=>{
  clearTransition();setCurrentUrl(slides[0]?.image_url || '');setPreviousUrl(null);setRevealed(true)
  setPending(null);setFailed([]);setReady([]);decoded.current.clear();decoding.current.clear()
 },[signature,clearTransition])
 const finishTransition=useCallback(()=>{clearTransition();setPreviousUrl(null);setRevealed(true)},[clearTransition])
 const markLoaded=(node:HTMLImageElement,url:string)=>{
  if(decoded.current.has(url) || decoding.current.has(url))return
  decoding.current.add(url)
  const complete=()=>{
   decoding.current.delete(url)
   if(!mounted.current || !node.isConnected || node.getAttribute('src')!==url)return
   decoded.current.add(url);setReady(values=>values.includes(url)?values:[...values,url])
  }
  if(typeof node.decode==='function')node.decode().then(complete).catch(()=>{
   decoding.current.delete(url)
   if(mounted.current && node.isConnected && node.getAttribute('src')===url)setFailed(values=>values.includes(url)?values:[...values,url])
  })
  else complete()
 }
 const showSlide=useCallback((url:string)=>{
  if(url===activeUrl){setPending(null);return}
  clearTransition();setPending(null)
  setCurrentUrl(url)
  if(fade>0 && decoded.current.has(activeUrl)){
   setPreviousUrl(activeUrl);setRevealed(false)
   frame.current=window.requestAnimationFrame(()=>{
    frame.current=null;setRevealed(true)
    // transitionend is primary; this delayed fallback covers transitions suppressed by the browser.
    finishTimer.current=window.setTimeout(finishTransition,fade+100)
   })
  }else{setPreviousUrl(null);setRevealed(true)}
 },[activeUrl,fade,clearTransition,finishTransition])
 useEffect(()=>{
  if(!pending)return
  if(!valid.some(slide=>slide.image_url===pending.url)){
   const next=valid[(current+1)%valid.length]
   setPending(next && next.image_url!==activeUrl?{...pending,url:next.image_url}:null)
   return
  }
  if(!pending.manual && !canPlay){setPending(null);return}
  if(visible && (pending.manual || canPlay) && ready.includes(pending.url))showSlide(pending.url)
 },[pending,ready,canPlay,visible,signature,failed,activeUrl,current,showSlide])
 useEffect(()=>{
  const media=typeof window.matchMedia==='function'?window.matchMedia('(prefers-reduced-motion: reduce)'):null
  const update=()=>setReduced(Boolean(media?.matches))
  update();media?.addEventListener?.('change',update)
  return ()=>media?.removeEventListener?.('change',update)
 },[])
 useEffect(()=>{
  const element=root.current
  if(!element)return
  let intersecting=true
  const surface=element.closest('.journey-popup-overlay')
  const update=()=>setVisible(intersecting && !document.hidden && (!surface || surface.getAttribute('data-open')!=='false'))
  const mutation=new MutationObserver(update)
  if(surface)mutation.observe(surface,{attributes:true,attributeFilter:['data-open']})
  const intersection=typeof IntersectionObserver!=='undefined'?new IntersectionObserver(entries=>{intersecting=entries[0]?.isIntersecting!==false;update()}):null
  intersection?.observe(element)
  document.addEventListener('visibilitychange',update);update()
  return ()=>{mutation.disconnect();intersection?.disconnect();document.removeEventListener('visibilitychange',update)}
 },[Boolean(valid.length)])
 useEffect(()=>{
  if(!canPlay || previousUrl || pending)return
  const timer=window.setTimeout(()=>setPending({url:valid[(current+1)%valid.length].image_url,manual:false}),delay)
  return ()=>window.clearTimeout(timer)
 },[canPlay,delay,valid.length,signature,current,previousUrl,pending])

 if(!valid.length)return <div role="img" aria-label="Image unavailable" style={{...imageStyle,backgroundColor:'#f1f5f9'}} />
 const filling=imageStyle.height==='100%'
 const go=(direction:number)=>{setPaused(true);setPending({url:valid[(current+direction+valid.length)%valid.length].image_url,manual:true})}
 const stop=(event:React.MouseEvent)=>{event.preventDefault();event.stopPropagation()}
 const controlStyle:React.CSSProperties={minWidth:32,minHeight:32,border:0,borderRadius:999,backgroundColor:'rgba(0,0,0,.65)',color:'#fff',fontSize:12,lineHeight:1,padding:'8px 10px',cursor:'pointer'}
 const images=<>
  {!filling && <img decoding="async" src={valid[0].image_url} alt="" aria-hidden="true" style={{display:'block',width:'100%',height:'auto',visibility:'hidden'}} />}
  {valid.map(slide=>{
   const isActive=slide.image_url===activeUrl,isPrevious=slide.image_url===previousUrl
   return <img decoding="async" key={slide.id||slide.image_url} src={slide.image_url} alt={slide.alt||''} aria-hidden={!isActive} data-slide-active={isActive} data-slide-previous={isPrevious}
    ref={node=>{if(node?.complete && node.naturalWidth>0)markLoaded(node,slide.image_url)}}
    onLoad={event=>markLoaded(event.currentTarget,slide.image_url)}
    onError={()=>setFailed(values=>values.includes(slide.image_url)?values:[...values,slide.image_url])}
    onTransitionEnd={event=>{if(event.target===event.currentTarget && event.propertyName==='opacity' && isActive && revealed)finishTransition()}}
    style={{...imageStyle,position:'absolute',inset:0,display:'block',width:'100%',height:'100%',objectFit:imageStyle.objectFit||'cover',zIndex:isActive?2:isPrevious?1:0,
     opacity:isPrevious?1:isActive && revealed?1:0,transition:isActive && previousUrl?`opacity ${fade}ms ease`:'none',pointerEvents:isActive?'auto':'none'}} />
  })}
 </>
 return <div ref={root} className="kibu-popup-slideshow" role="group" aria-roledescription="slideshow" aria-label="Image slideshow" onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)} onFocusCapture={()=>setFocused(true)} onBlurCapture={event=>{if(!event.currentTarget.contains(event.relatedTarget as Node))setFocused(false)}} style={{position:'relative',display:filling?'block':'inline-block',width:imageStyle.width||'100%',height:filling?'100%':undefined,verticalAlign:'middle',overflow:'hidden',borderTopLeftRadius:imageStyle.borderTopLeftRadius,borderTopRightRadius:imageStyle.borderTopRightRadius,borderBottomRightRadius:imageStyle.borderBottomRightRadius,borderBottomLeftRadius:imageStyle.borderBottomLeftRadius}}>
  {href?<a href={href} style={{display:'block',height:filling?'100%':undefined,lineHeight:0}}>{images}</a>:images}
  {valid.length>1 && (controls || showPlayPause) && <div data-popup-preview-control onKeyDown={event=>event.stopPropagation()} style={{position:'absolute',bottom:8,right:8,display:'flex',alignItems:'center',gap:4,zIndex:3}}>
   {controls && <><button type="button" style={controlStyle} aria-label="Previous slide" onClick={event=>{stop(event);go(-1)}}><ChevronLeft size={14} aria-hidden="true" /></button><span aria-live="off" style={{...controlStyle,display:'inline-flex',alignItems:'center',cursor:'default'}}>{current+1}/{valid.length}</span><button type="button" style={controlStyle} aria-label="Next slide" onClick={event=>{stop(event);go(1)}}><ChevronRight size={14} aria-hidden="true" /></button></>}
   {showPlayPause && <button type="button" style={controlStyle} aria-label={manualPause?'Play slideshow':'Pause slideshow'} onClick={event=>{stop(event);if(manualPause){setPaused(false);setRequestedPlayback(true)}else setPaused(true)}}>{manualPause?'Play':'Pause'}</button>}
  </div>}
 </div>
}
